import type { ReactNode } from 'react'
import { CardCornerDebugInfo } from 'src/components/Admin/UserContentInfo/CardCornerDebugInfo'
import SingleMediaViewer from 'src/components/Media/SingleMediaViewer'
import type { ArtworkDetailFragment } from 'src/services/apollo/generated/graphql'
import { isAnimatedArtwork } from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import { getImagePublicUrl, getImageThumbnailUrl } from '../../../utils/media'
import {
  getArtworkAlt,
  getImageDownloadFileExt,
  getImageDownloadFilename,
} from '../utils'
import styles from './index.module.css'
import { getArtworkVideoHasAudio } from './videoAudio'

export function ArtworkDetailImage(props: {
  artwork: ArtworkDetailFragment
  actions?: ReactNode
}) {
  const { artwork } = props

  const mediaType =
    getImageDownloadFileExt({ artwork, media: artwork.media }) === 'mp4'
      ? 'video'
      : 'image'
  const hasAudio = getArtworkVideoHasAudio(artwork.extra)
  return (
    <div className={styles['artwork-image-bg']}>
      <div
        className={cx(
          styles['artwork-image-container'],
          mediaType === 'video' ? styles['video'] : undefined,
        )}
      >
        <SingleMediaViewer
          scene="artwork-detail"
          imgClassName={styles['artwork-image-element']}
          artworkId={artwork.id}
          originalUrl={getImagePublicUrl(artwork.mediaId, artwork.media)}
          thumbnailUrl={getImageThumbnailUrl(artwork.mediaId, artwork.media)}
          showFallbackThumbnail
          containerClassName="w-fit h-full"
          width={artwork.media?.width}
          height={artwork.media?.height}
          author={artwork.author}
          alt={getArtworkAlt(artwork)}
          fileName={
            getImageDownloadFilename({ artwork }) +
            '.' +
            getImageDownloadFileExt({ artwork, media: artwork.media })
          }
          mediaType={mediaType}
          skipDownloadConversion={isAnimatedArtwork(artwork)}
          videoMediaId={artwork.videoMediaId}
          mediaId={artwork.mediaId}
          preferVideo
          videoHasAudio={hasAudio}
          // Detail hero is the LCP candidate. Without these hints the SSR
          // HTML still emits the <img> tag, but the browser deprioritises
          // the request after parsing scripts/styles, leaving the hero
          // slot visibly empty for ~1s on slow networks even though
          // the markup is already there. `loading="eager"` +
          // `fetchpriority="high"` (paired with the `<link rel="preload"
          // as="image" fetchpriority="high">` injected from route meta)
          // moves the request onto the critical path.
          priority
        />
        <CardCornerDebugInfo
          extra={artwork?.extra}
          paidCredit={artwork?.paidCredit}
          aesScore={artwork?.aesScore}
        >
          {content => (
            <div className="absolute top-2 left-2 bg-black/60 backdrop-blur rounded-xl px-2 py-1.5">
              {content}
            </div>
          )}
        </CardCornerDebugInfo>
        {props.actions}
      </div>
    </div>
  )
}

export default ArtworkDetailImage
